import { StateCreator, create } from "zustand";
import type { TypeChangeConfirm } from "@/components/common/TypeChangeConfirmDialog";
import { clear_websocket, ClientApp, UserIdentification } from "target_wasm";

import {
  CanvasFocusType,
  CanvasHoverType,
  SelectedRow,
  FocusedCell,
} from "@/components/canvas/IFocus";
import type { CanvasFocusPattern, CanvasMode, CommitStateViz } from "@/exports";
import type { CursorMode } from "@/lib/cursorTools";
import type { CanvasEditType } from "@/components/canvas/stagedEdit/StagedEditType";
import { createLogger } from "debug/DebugingLogger";
import { closeOpenStores } from "stores/createOpenStore";
import { createWebWorkerState, WebWorkerState } from "./WebWorkerStore";
const log = createLogger("AppStore");

/** WASM pushes a fresh object per emit (hover/wheel frames included), so
 *  reference equality never holds — compare structurally. The payloads are
 *  small; a stringify is far cheaper than a store-wide listener walk. */
function pushedInfoUnchanged<T>(current: T | null, next: T | null): boolean {
  if (next === null || current === null) return next === current;
  return JSON.stringify(current) === JSON.stringify(next);
}
// State of App
export interface AppStateSlice {
  clientApp: ClientApp | null;
  websocket: WebSocket | null;
  canvasInput: CanvasEditType | null;
  /// An open cell editor's way of surviving a click on the canvas, which
  /// otherwise discards the staged edit. Registered by the editor while it is
  /// mounted; the canvas runs it instead of discarding.
  canvasInputCommit: (() => void) | null;
  canvasFocus: CanvasFocusType;
  selectedRow: SelectedRow | null;
  focusedCell: FocusedCell | null;
  canvasHover: CanvasHoverType | null;
  canvasColumnSelection: string[] | null;
  modelStructureVersion: number;
  detailVersion: number;
  rowInspectorVersion: number;
  tableHistoryInvalidation: number;
  /// Which canvas is up. Pushed by WASM on every mode change (`setCanvasMode`)
  /// — never derived at a call site, or the two would drift. Every overlay
  /// gates on it, since each belongs to exactly one mode.
  canvasMode: CanvasMode;
  isGraphView: boolean;
  isRowGraphView: boolean;
  /** The locked pointer tool, pushed by WASM. */
  cursorMode: CursorMode;
  setCursorMode: (mode: CursorMode) => void;
  /// Rows selected on the tabular canvas, pushed by WASM whenever it changes.
  selectedRowCount: number;
  /// Shape of the engine's focus, pushed by WASM. Distinguishes a focused cell
  /// from a single-column selection, which the column selection alone cannot.
  canvasFocusPattern: CanvasFocusPattern;
  /// Commit per TABLE, pushed by WASM on change. Two views of one table share a
  /// commit, so both read the same entry. `typeChangeStaged`: the staged commit
  /// retypes a column, so a press on Commit asks first.
  commitByTable: Record<
    string,
    { state: CommitStateViz; typeChangeStaged: boolean }
  >;
  /** The investigation's node count, and how many more it can hold. */
  rowGraphSize: number;
  rowGraphHeadroom: number;
  /** Whether the camera can zoom further, pushed by WASM after any move. */
  canZoomIn: boolean;
  canZoomOut: boolean;
  modelScheduledDeletionDate: number | null;
  tableDeletionStates: Record<
    string,
    { hard_deletion: boolean; is_scheduled: boolean }
  >;
  bumpModelStructureVersion: () => void;
  bumpDetailVersion: () => void;
  bumpRowInspectorVersion: () => void;
  permissionsVersion: number;
  bumpPermissionsVersion: () => void;
  linksVersion: number;
  bumpLinksVersion: () => void;
  pendingImports: {
    postgres: ReadonlySet<string>;
    gsheets: ReadonlySet<string>;
    bucket: ReadonlySet<string>;
    sqlite: ReadonlySet<string>;
  };
  markImportPending: (
    type: "postgres" | "gsheets" | "bucket" | "sqlite",
    key: string,
  ) => void;
  clearImportPending: (
    type: "postgres" | "gsheets" | "bucket" | "sqlite",
    key: string,
  ) => void;
  linksFetchStatus: Record<string, "loaded" | "denied" | "error">;
  setLinksFetchStatus: (
    tableId: string,
    status: "loaded" | "denied" | "error",
  ) => void;
  setCanvasInput: (props: CanvasEditType | null) => void;
  setCanvasInputCommit: (commit: (() => void) | null) => void;
  setCanvasFocus: (props: CanvasFocusType) => void;
  setSelectedRow: (row: SelectedRow | null) => void;
  setFocusedCell: (cell: FocusedCell | null) => void;
  setCanvasHover: (props: CanvasHoverType | null) => void;
  setCanvasColumnSelection: (columnIds: string[] | null) => void;
  setModelScheduledDeletionDate: (date: number | null) => void;
  setClientApp: (ca: ClientApp) => void;
  resetClientApp: () => void;
  setWebsocket: (socket: WebSocket | null) => void;
  invalidateTableHistory: () => void;
  setCanvasMode: (mode: CanvasMode) => void;
  setSelectedRowCount: (count: number) => void;
  setCanvasFocusPattern: (pattern: CanvasFocusPattern) => void;
  setCanvasCommitState: (
    tableId: string,
    state: CommitStateViz,
    typeChangeStaged?: boolean,
  ) => void;
  setRowGraph: (size: number, headroom: number) => void;
  setZoomRange: (canZoomIn: boolean, canZoomOut: boolean) => void;
  setTableDeletionState: (
    tableId: string,
    hard_deletion: boolean,
    is_scheduled: boolean,
  ) => void;
  resetTableDeletionStates: () => void;
  conflictDialog: { title: string; message: string } | null;
  setConflictDialog: (info: { title: string; message: string } | null) => void;
  typeChangeConfirm: TypeChangeConfirm | null;
  setTypeChangeConfirm: (confirm: TypeChangeConfirm | null) => void;
  importCounts: Record<string, number>;
  setImportCount: (tableId: string, count: number) => void;
  linkCounts: Record<string, number>;
  setLinkCount: (tableId: string, count: number) => void;
  parentModels: Record<
    string,
    {
      parent_model_id: string;
      parent_model_name: string;
      parent_model_color?: string;
      parent_model_icon_index?: number;
    }
  >;
  setParentModel: (
    tableId: string,
    parentModelId: string,
    parentModelName: string,
    parentModelColor?: string,
    parentModelIconIndex?: number,
  ) => void;
  /// Cached `SourceInfo` per table — replaces the old per-type
  /// `postgresSources` and `sheetSources` slices. Keyed by `table_id`,
  /// invalidated on table-version bump (handled by the consumer hook).
  tableSources: Record<
    string,
    import("../detailsSide/utils/Item/sourceInfo").SourceInfo
  >;
  setTableSource: (
    tableId: string,
    info: import("../detailsSide/utils/Item/sourceInfo").SourceInfo,
  ) => void;
  tablePermissions: Record<string, string[]>;
  setTablePermissions: (tableId: string, permissions: string[]) => void;
  overflowIcon: import("../../../exports").OverflowIconInfo | null;
  overflowExpanded: boolean;
  setOverflowIcon: (
    icon: import("../../../exports").OverflowIconInfo | null,
  ) => void;
  setOverflowExpanded: (expanded: boolean) => void;
  /// Read-only formula peek published by WASM on a computed-header click;
  /// cleared (null) on pan/zoom, overlay open, or model change.
  formulaPeek: import("../../../exports").FormulaPeekInfo | null;
  setFormulaPeek: (
    peek: import("../../../exports").FormulaPeekInfo | null,
  ) => void;
  /// Chips over a selected graph node, published by WASM at discrete changes
  /// (selection, expand/collapse, pointer release) and cleared on pan/zoom —
  /// the anchors are absolute screen px, so a viewport change invalidates them.
  graphImportChip: import("../../../exports").GraphImportChipInfo | null;
  setGraphImportChip: (
    chip: import("../../../exports").GraphImportChipInfo | null,
  ) => void;
  graphExpandChip: import("../../../exports").GraphExpandChipInfo | null;
  setGraphExpandChip: (
    chip: import("../../../exports").GraphExpandChipInfo | null,
  ) => void;
  /// Docked row-graph overlay (focused node + linked tables) published by WASM
  /// on focus/expansion changes; cleared (null) on deselect or exit. Camera-
  /// independent, so it's pushed rather than polled.
  rowGraphOverlay: import("../../../exports").RowGraphOverlayInfo | null;
  setRowGraphOverlay: (
    overlay: import("../../../exports").RowGraphOverlayInfo | null,
  ) => void;
  /// The selected cell's action icon (open its link, view its files).
  cellActionIcon: import("../../../exports").CellActionIconInfo | null;
  setCellActionIcon: (
    icon: import("../../../exports").CellActionIconInfo | null,
  ) => void;
  /// The table-view ornament (add row/column, commit, discard) the canvas last
  /// drew hovered. WASM pushes only changes.
  ornamentHint: import("../../../exports").OrnamentHintInfo | null;
  setOrnamentHint: (
    hint: import("../../../exports").OrnamentHintInfo | null,
  ) => void;
  /// True while an import-panel row is dragged toward the canvas. Drives the
  /// "Drop to import" overlay; set on dragstart, cleared on dragend/drop.
  canvasDragActive: boolean;
  setCanvasDragActive: (active: boolean) => void;
  /// True only while that drag is over the canvas DropZone itself — the
  /// overlay's accent wash uses it as the "release to drop" cue. Maintained
  /// by the DropZone's dragenter/dragleave; ending the drag clears it.
  canvasDragOver: boolean;
  setCanvasDragOver: (over: boolean) => void;
}

/** What a ClientApp pushes, or mirrors from it. A new one starts here and
 *  pushes only changes, so a reset that missed a field would leave it showing
 *  for the next model. */
const clientAppMirror = () =>
  ({
    canvasInput: null,
    canvasInputCommit: null,
    canvasFocus: { type: "Model" },
    selectedRow: null,
    focusedCell: null,
    canvasColumnSelection: null,
    canvasMode: "tabular",
    isGraphView: false,
    isRowGraphView: false,
    cursorMode: "select",
    selectedRowCount: 0,
    canvasFocusPattern: "noFocus",
    commitByTable: {},
    rowGraphSize: 0,
    rowGraphHeadroom: 0,
    canZoomIn: true,
    canZoomOut: true,
    conflictDialog: null,
    typeChangeConfirm: null,
    overflowIcon: null,
    overflowExpanded: false,
    formulaPeek: null,
    graphImportChip: null,
    graphExpandChip: null,
    rowGraphOverlay: null,
    cellActionIcon: null,
    ornamentHint: null,
  }) satisfies Partial<AppStateSlice>;

const createAppStateSlice: StateCreator<
  AppStateSlice,
  [],
  [],
  AppStateSlice
> = (set, get) => ({
  ...clientAppMirror(),
  clientApp: null,
  websocket: null,
  canvasHover: null,
  modelStructureVersion: 0,
  detailVersion: 0,
  rowInspectorVersion: 0,
  tableHistoryInvalidation: 0,
  setCursorMode(mode) {
    set((state) => (state.cursorMode === mode ? state : { cursorMode: mode }));
  },
  modelScheduledDeletionDate: null,
  tableDeletionStates: {},
  bumpModelStructureVersion() {
    set((state) => ({
      modelStructureVersion: state.modelStructureVersion + 1,
    }));
  },
  bumpRowInspectorVersion() {
    set((state) => ({ rowInspectorVersion: state.rowInspectorVersion + 1 }));
  },
  bumpDetailVersion() {
    set((state) => ({ detailVersion: state.detailVersion + 1 }));
  },
  permissionsVersion: 0,
  bumpPermissionsVersion() {
    set((state) => ({ permissionsVersion: state.permissionsVersion + 1 }));
  },
  linksVersion: 0,
  bumpLinksVersion() {
    set((state) => ({ linksVersion: state.linksVersion + 1 }));
  },
  pendingImports: {
    postgres: new Set<string>(),
    gsheets: new Set<string>(),
    bucket: new Set<string>(),
    sqlite: new Set<string>(),
  },
  markImportPending(type, key) {
    set((state) => {
      const current = state.pendingImports[type];
      if (current.has(key)) return state;
      const next = new Set(current);
      next.add(key);
      return {
        pendingImports: { ...state.pendingImports, [type]: next },
      };
    });
  },
  clearImportPending(type, key) {
    set((state) => {
      const current = state.pendingImports[type];
      if (!current.has(key)) return state;
      const next = new Set(current);
      next.delete(key);
      return {
        pendingImports: { ...state.pendingImports, [type]: next },
      };
    });
  },
  canvasDragActive: false,
  setCanvasDragActive(active) {
    // Ending a drag atomically drops the hover cue with it.
    set(
      active
        ? { canvasDragActive: true }
        : { canvasDragActive: false, canvasDragOver: false },
    );
  },
  canvasDragOver: false,
  setCanvasDragOver(over) {
    set({ canvasDragOver: over });
  },
  linksFetchStatus: {},
  setLinksFetchStatus(tableId, status) {
    set((state) => ({
      linksFetchStatus: { ...state.linksFetchStatus, [tableId]: status },
    }));
  },
  setCanvasInput(props) {
    set({ canvasInput: props });
  },
  setCanvasInputCommit(commit) {
    set({ canvasInputCommit: commit });
  },
  setCanvasFocus(props) {
    set({
      canvasFocus:
        props.type === "Row"
          ? { ...props, row_id: BigInt(props.row_id) }
          : props,
    });
  },
  setSelectedRow(row) {
    set({ selectedRow: row && { ...row, row_id: BigInt(row.row_id) } });
  },
  setFocusedCell(cell) {
    set({ focusedCell: cell && { ...cell, row_id: BigInt(cell.row_id) } });
  },
  setCanvasHover(props) {
    set({ canvasHover: props });
  },
  setCanvasColumnSelection(columnIds) {
    set({ canvasColumnSelection: columnIds });
  },
  setModelScheduledDeletionDate(date: number | null) {
    set({ modelScheduledDeletionDate: date });
  },
  setClientApp: (ca: ClientApp | null) => {
    log.info("Updated client App");
    set({ clientApp: ca });
  },
  resetClientApp: () => {
    const freed = get().clientApp;
    set({
      ...clientAppMirror(),
      clientApp: null,
      modelScheduledDeletionDate: null,
      parentModels: {},
      tableSources: {},
    });
    closeOpenStores();
    // React runs a deleted tree's cleanups parent-first, so a child's cleanup
    // may still call the client it captured after this one has run.
    if (freed) {
      queueMicrotask(() => {
        try {
          clear_websocket();
          freed.free();
          log.info("Successfully freed client app");
        } catch (error) {
          log.warning("Error freeing client app:", error);
        }
      });
    }
    log.info("reset client app");
  },
  setWebsocket: (ws: WebSocket | null) => {
    log.info("Set Ws Connection");
    set({ websocket: ws });
  },
  invalidateTableHistory() {
    set((state) => ({
      tableHistoryInvalidation: state.tableHistoryInvalidation + 1,
    }));
  },
  setCanvasMode(mode: CanvasMode) {
    set((state) =>
      state.canvasMode === mode
        ? state
        : {
            canvasMode: mode,
            isGraphView: mode === "graph",
            isRowGraphView: mode === "rowGraph",
          },
    );
  },
  setSelectedRowCount(count: number) {
    set((state) =>
      state.selectedRowCount === count ? state : { selectedRowCount: count },
    );
  },
  setCanvasCommitState(
    tableId: string,
    state: CommitStateViz,
    typeChangeStaged = false,
  ) {
    set((s) => {
      const prev = s.commitByTable[tableId];
      return prev?.state === state && prev.typeChangeStaged === typeChangeStaged
        ? s
        : {
            commitByTable: {
              ...s.commitByTable,
              [tableId]: { state, typeChangeStaged },
            },
          };
    });
  },
  setCanvasFocusPattern(pattern: CanvasFocusPattern) {
    set((state) =>
      state.canvasFocusPattern === pattern
        ? state
        : { canvasFocusPattern: pattern },
    );
  },
  setRowGraph(size, headroom) {
    set({ rowGraphSize: size, rowGraphHeadroom: headroom });
  },
  setZoomRange(canZoomIn, canZoomOut) {
    set({ canZoomIn, canZoomOut });
  },
  setTableDeletionState(
    tableId: string,
    hard_deletion: boolean,
    is_scheduled: boolean,
  ) {
    set((state) => {
      if (is_scheduled) {
        return {
          tableDeletionStates: {
            ...state.tableDeletionStates,
            [tableId]: { hard_deletion, is_scheduled },
          },
        };
      }
      const { [tableId]: _, ...rest } = state.tableDeletionStates;
      return { tableDeletionStates: rest };
    });
  },
  resetTableDeletionStates() {
    set({ tableDeletionStates: {} });
  },
  setConflictDialog(info) {
    set({ conflictDialog: info });
  },
  setTypeChangeConfirm(confirm) {
    set({ typeChangeConfirm: confirm });
  },
  importCounts: {},
  setImportCount(tableId, count) {
    set((state) => ({
      importCounts: { ...state.importCounts, [tableId]: count },
    }));
  },
  linkCounts: {},
  setLinkCount(tableId, count) {
    set((state) => ({
      linkCounts: { ...state.linkCounts, [tableId]: count },
    }));
  },
  parentModels: {},
  setParentModel(
    tableId,
    parentModelId,
    parentModelName,
    parentModelColor,
    parentModelIconIndex,
  ) {
    set((state) => ({
      parentModels: {
        ...state.parentModels,
        [tableId]: {
          parent_model_id: parentModelId,
          parent_model_name: parentModelName,
          parent_model_color: parentModelColor,
          parent_model_icon_index: parentModelIconIndex,
        },
      },
    }));
  },
  tableSources: {},
  setTableSource(tableId, info) {
    set((state) => ({
      tableSources: { ...state.tableSources, [tableId]: info },
    }));
  },
  tablePermissions: {},
  setTablePermissions(tableId, permissions) {
    set((state) => ({
      tablePermissions: { ...state.tablePermissions, [tableId]: permissions },
    }));
  },
  setOverflowIcon(icon) {
    // WASM re-emits per hover frame; a no-op set walks every store listener
    // (and would collapse an expanded overflow). Same guard as setCellActionIcon.
    if (pushedInfoUnchanged(get().overflowIcon, icon)) return;
    set({ overflowIcon: icon, overflowExpanded: false });
  },
  setOverflowExpanded(expanded) {
    set({ overflowExpanded: expanded });
  },
  setFormulaPeek(peek) {
    if (pushedInfoUnchanged(get().formulaPeek, peek)) return;
    set({ formulaPeek: peek });
  },
  setGraphImportChip(chip) {
    if (pushedInfoUnchanged(get().graphImportChip, chip)) return;
    set({ graphImportChip: chip });
  },
  setGraphExpandChip(chip) {
    if (pushedInfoUnchanged(get().graphExpandChip, chip)) return;
    set({ graphExpandChip: chip });
  },
  setRowGraphOverlay(overlay) {
    set({ rowGraphOverlay: overlay });
  },
  setCellActionIcon(icon) {
    if (pushedInfoUnchanged(get().cellActionIcon, icon)) return;
    set({ cellActionIcon: icon });
  },
  setOrnamentHint(hint) {
    set({ ornamentHint: hint });
  },
});

interface AppBarState {
  activeUsers: UserIdentification[];
  setActiveUsers: (users: UserIdentification[]) => void;
  followingUserId: string | null;
  setFollowingUserId: (id: string | null) => void;
  /** Collaborators sharing the spot under the pointer, named by a tooltip. */
  presenceHint: import("../../../exports").PresenceHintInfo | null;
  /** Collaborators and follows belong to the model being left. */
  resetPresence: () => void;
}
const createAppBarState: StateCreator<AppBarState> = (set, _get) => ({
  activeUsers: [],
  setActiveUsers: (users: UserIdentification[]) => {
    log.info("Updated active users:", users.length);
    set({ activeUsers: users });
  },
  followingUserId: null,
  setFollowingUserId: (id: string | null) => {
    set({ followingUserId: id });
  },
  presenceHint: null,
  resetPresence: () =>
    set({
      activeUsers: [],
      followingUserId: null,
      presenceHint: null,
    }),
});

export const useBoundStore = create<
  AppStateSlice & AppBarState & WebWorkerState
>()((...a) => ({
  ...createAppStateSlice(...a),
  ...createAppBarState(...a),
  ...createWebWorkerState(...a),
}));
